import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import { Button, BaseDialog } from '@app/components';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import ArrowRight from '@app/assets/icons/arrow-right.svg?react';
import InfoBlueIcon from '@app/assets/icons/info-blue.svg?react';
import CustomerBlueIcon from '@app/assets/icons/customer-blue.svg?react';
import MixMessageIcon from '@app/assets/icons/mix-message.svg?react';
import NewCampaignIcon from '@app/assets/icons/new-campaign.svg?react';
import { CreateCampaign as CreateCampaignRoutes } from '@app/utils/consts';
import { useMemo } from 'react';
import { CreateCampaignSettings } from './CreateCampaignSettings';
import { CreateCampaignCustomers } from './CreateCampaignCustomers';
import { CreateCampaignCampaign } from './CreateCampaignCampaign';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { useFormChangeDetection } from './useFormChangeDetection';
import CloseIcon from '@app/assets/icons/close.svg?react';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';

export const CreateCampaign = ({ page }: { page: CreateCampaignRoutes }) => {
  const navigate = useNavigate();
  const campaignCreationForm = useForm();
  const { interceptNavigation } = useNavigationContext();
  const dispatch = useAppDispatch();

  // Use the custom hook for form change detection
  const {
    showConfirmationModal,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useFormChangeDetection(campaignCreationForm);

  // Check if we're editing a draft campaign
  const isEditing = campaignCreationForm.watch('campaignId');

  const handleNext = () => {
    // Save current form data before navigating
    const values = campaignCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));

    switch (page) {
      case CreateCampaignRoutes.SETTINGS: {
        navigate('/campaigns/create/customers');
        break;
      }
      case CreateCampaignRoutes.CUSTOMERS: {
        navigate('/campaigns/create/campaign');
        break;
      }
      case CreateCampaignRoutes.CAMPAIGN: {
        break;
      }
      default: {
        navigate('/campaigns/create/settings');
      }
    }
  };

  const handlePrevious = () => {
    const values = campaignCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
    switch (page) {
      case CreateCampaignRoutes.SETTINGS: {
        break;
      }
      case CreateCampaignRoutes.CUSTOMERS: {
        navigate('/campaigns/create/settings');
        break;
      }
      case CreateCampaignRoutes.CAMPAIGN: {
        navigate('/campaigns/create/customers');
        break;
      }
      default: {
        navigate('/campaigns/create/settings');
      }
    }
  };

  // Handle direct navigation for step buttons
  const handleStepNavigation = (targetPath: string) => {
    const values = campaignCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
    navigate(targetPath);
  };

  // Handle confirmation modal actions
  const handleConfirmNavigationAction = () => {
    const targetPath = handleConfirmNavigation();
    if (targetPath) {
      navigate(targetPath);
    }
  };

  const renderPage = useMemo(() => {
    switch (page) {
      case CreateCampaignRoutes.SETTINGS: {
        return <CreateCampaignSettings form={campaignCreationForm} />;
      }
      case CreateCampaignRoutes.CUSTOMERS: {
        return <CreateCampaignCustomers form={campaignCreationForm} />;
      }
      case CreateCampaignRoutes.CAMPAIGN: {
        return <CreateCampaignCampaign form={campaignCreationForm} />;
      }
      default: {
        return <CreateCampaignSettings form={campaignCreationForm} />;
      }
    }
  }, [page]);

  return (
    <div className="relative">
      <div className="bg-white border-b border-b-base-ghostWhite template-editor-campaign-header">
        <div className="container mx-auto p-4 flex items-center">
          <h2 className="font-bold text-[28px] flex items-center">
            <button
              onClick={() => interceptNavigation('/campaigns')}
              className="mr-2"
            >
              <ArrowBackIcon />
            </button>
            {isEditing ? 'Edit Campaign' : 'Create Campaign'}
            <div className="bg-[#F6F7FA] rounded-md p-2 ml-2">
              <NewCampaignIcon />
            </div>
          </h2>
        </div>
        <hr />
        <div className="container mx-auto p-4 flex justify-between items-center">
          <Button
            kind="secondary"
            onClick={handlePrevious}
            className="min-w-fit"
          >
            <ArrowLeft />
          </Button>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleStepNavigation('/campaigns/create/settings')}
              className={clsx(
                'border border-base-ghostWhite px-4 py-1 rounded-full text-sm flex items-center gap-2',
                {
                  'border-brand-baseBlue bg-brand-lightBlue4':
                    page === CreateCampaignRoutes.SETTINGS,
                }
              )}
            >
              <InfoBlueIcon width={16} height={16} />
              1. Settings
            </button>
            <ArrowRight />
            <button
              onClick={() =>
                handleStepNavigation('/campaigns/create/customers')
              }
              className={clsx(
                'border border-base-ghostWhite px-4 py-1 rounded-full text-sm flex items-center gap-2',
                {
                  'border-brand-baseBlue bg-brand-lightBlue4':
                    page === CreateCampaignRoutes.CUSTOMERS,
                }
              )}
              disabled={!campaignCreationForm.formState.isValid}
            >
              <CustomerBlueIcon width={16} height={16} />
              2. Customers
            </button>
            <ArrowRight />
            <button
              onClick={() => handleStepNavigation('/campaigns/create/campaign')}
              className={clsx(
                'border border-base-ghostWhite px-4 py-1 rounded-full text-sm flex items-center gap-2',
                {
                  'border-brand-baseBlue bg-brand-lightBlue4':
                    page === CreateCampaignRoutes.CAMPAIGN,
                }
              )}
              disabled={!campaignCreationForm.formState.isValid}
            >
              <MixMessageIcon width={16} height={16} />
              3. Campaign
            </button>
          </div>
          <Button
            onClick={handleNext}
            className="min-w-fit"
            disabled={
              page === CreateCampaignRoutes.CAMPAIGN ||
              !campaignCreationForm.formState.isValid
            }
          >
            <ArrowRight className="[&_path]:stroke-[white]" />
          </Button>
        </div>
      </div>
      <div>{renderPage}</div>

      <BaseDialog open={showConfirmationModal} onClose={handleCancelNavigation}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <CloseIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Unsaved Changes</h5>
          </div>
          <div>
            <button type="button" onClick={handleCancelNavigation}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-base-davyGray">
            You have unsaved changes in your campaign. Are you sure you want to
            leave this page?
          </p>
          <div className="flex items-center gap-4 justify-center py-4">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={handleCancelNavigation}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              type="button"
              onClick={handleConfirmNavigationAction}
            >
              Leave Page
            </Button>
          </div>
        </div>
      </BaseDialog>
    </div>
  );
};
